// The suite's shared footer.
//
// REFERENCE: VehicleDebugger's `.site-footer` in public/style.css and its markup in
// public/index.html — a centred, wrapping row of muted parts, each part joining its fields
// with " · ".
//
// VehicleDebugger shows four parts: brand, session/user counts, uptime and availability, and
// the build stamp. The middle two come from its Fastify backend; the React tools have no
// backend and no telemetry, so they carry the two parts they can actually source — the brand
// line and the build stamp.
//
// The stamp arrives as props because each tool sources it differently: EngineeringTools bakes
// it in through Vite `define`, the EventManager through Create React App's REACT_APP_* env
// vars. Keeping that out of here lets this file stay identical in both.

import { Box, Typography } from '@mui/material'
import { footerTokens } from '../theme/appTheme'

interface AppFooterProps {
  /** e.g. "NEURA Mobile Robots · internes Engineering-Portal" */
  brand: string
  version?: string
  /** Hidden when absent or "unknown", matching VehicleDebugger's applyAppBuildInfo. */
  branch?: string
  /** ISO date (YYYY-MM-DD). */
  buildDate?: string
}

/** Mirrors VehicleDebugger's formatBuildDate: DD.MM.YYYY in both locales. */
function formatBuildDate(isoDate: string | undefined): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec((isoDate ?? '').trim())
  if (!match) return ''
  return `${match[3]}.${match[2]}.${match[1]}`
}

export function AppFooter({ brand, version, branch, buildDate }: AppFooterProps) {
  // Same order and omission rules as VehicleDebugger's applyAppBuildInfo.
  const buildParts = [
    version?.trim() ? `v${version.trim()}` : '',
    branch?.trim() && branch.trim() !== 'unknown' ? branch.trim() : '',
    formatBuildDate(buildDate),
  ].filter(Boolean)

  return (
    <Box
      component="footer"
      sx={{
        maxWidth: footerTokens.maxWidth,
        mx: 'auto',
        padding: footerTokens.padding,
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        textAlign: 'center',
        gap: footerTokens.gap,
      }}
    >
      <Typography
        component="span"
        sx={{ fontSize: footerTokens.fontSize, color: 'text.secondary' }}
      >
        {brand}
      </Typography>
      {buildParts.length > 0 && (
        <Typography
          component="span"
          sx={{ fontSize: footerTokens.fontSize, color: 'text.secondary', opacity: 0.9 }}
        >
          {buildParts.join(footerTokens.separator)}
        </Typography>
      )}
    </Box>
  )
}
